import { Controller, useForm } from 'react-hook-form'
import React, { FC, useEffect } from 'react'
import styles from './UserForm.module.scss'
import { yupResolver } from '@hookform/resolvers/yup'
import { useFormNotify } from '@components/formNotify/useFormNotify.tsx'
import * as yup from 'yup'
import { useUserContext } from '@logic/contexts/AppStore/UserContext.tsx'
import { UserF } from '@logic/contexts/Users/UsersFrontend.type.ts'
import { LxLoadingSpinner } from '@components/loader/loadingSpinner.tsx'
import LxCancelButton from '@src/components/cancelButton/LxCancelButton'
import LxSaveButton from '@src/components/saveButton/LxSaveButton'
import { useFormPersistence } from '@src/hooks/UseFormPersistence'
import { checkAccSettingsScope } from '@src/utils/accountSettings'
import PrivacyNotice from '@src/utils/PrivacyNotice'
import { Autocomplete, TextField } from '@mui/material'

const getRoleChoices = (userRole: string) => {
  const roleChoices = [
    { value: 'account_admin', label: 'Account Admin' },
    { value: 'admin', label: 'Admin' },
    { value: 'user', label: 'User' },
    { value: 'assistant', label: 'Assistant' },
  ]

  // Check currentUser's role and filter choices accordingly
  if (userRole == 'account_admin') {
    return roleChoices
  } else if (userRole == 'admin') {
    return roleChoices.filter((choice) => choice.value === 'admin' || choice.value === 'user' || choice.value === 'assistant')
  } else if (userRole == 'user') {
    return roleChoices.filter((choice) => choice.value === 'assistant')
  } else {
    return []
  }
}

export type UserFormType = {
  id?: string
  firstName: string
  lastName: string
  jobTitle?: string
  userRole?: string
  primaryEmail: string
}
export const defaultUserFormValues: UserFormType = {
  firstName: '',
  lastName: '',
  jobTitle: '',
  userRole: '',
  primaryEmail: '',
}

export const userFormValidation = yupResolver(
  yup
    .object()
    .shape({
      firstName: yup.string().required('First name is required'),
      lastName: yup.string().required('Last name is required'),
      jobTitle: yup.string(),
      userRole: yup.string().required('Role is required'),
      primaryEmail: yup.string().email('Must be valid email').required('Email is required'),
    })
    .required()
)

export const parseUserFormToBackend = (formData: UserFormType) => ({
  first_name: formData.firstName,
  last_name: formData.lastName,
  job_title: formData.jobTitle,
  user_role: formData.userRole,
  primary_email: formData.primaryEmail,
})

type Props = {
  onSubmit: (formData: UserFormType) => void
  user?: UserF | null
  isEdit?: boolean
  onCancel: () => void
  isSubmitting: boolean
}

export const UserForm: FC<Props> = ({ onSubmit, user, onCancel, isEdit, isSubmitting = false }) => {
  const { currentUser } = useUserContext() as any
  // Add the RBAC check for privacy notice
  const { canAccess: canSeePrivacyMessage, effectiveValue } = checkAccSettingsScope('privacy_notice')
  const accHasPrivacyMessage = effectiveValue === 'true'
  const {
    control,
    handleSubmit, 
    reset,
    formState: { errors, submitCount, isValid },
  } = useForm({
    defaultValues: user ? { ...user } : defaultUserFormValues,
    resolver: userFormValidation,
  })

  useFormNotify(errors, submitCount, isValid)

  useEffect(() => {
    // Reset the form when defaultValues change (for Edit scenario)
    if (user) {
      reset({ ...user })
    }
  }, [user, reset])

  // const formValues = watch()

  // useFormPersistence({
  //   formId: 'user',
  //   entityId: user?.id,
  //   defaultValues: user ? user : defaultUserFormValues,
  //   formValues,
  //   reset,
  // })

  return (
    <div className={styles.userForm}>
      {isSubmitting && (
        <div className={styles.LoadingOverlay}>
          <LxLoadingSpinner />
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name='firstName'
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <TextField
              fullWidth
              label='First Name'
              value={value || ''}
              onChange={onChange}
              error={!!error}
              helperText={error?.message}
              variant='outlined'
              size='medium'
              margin='normal'
              required
              className={styles.muiInputField}
            />
          )}
        />
        <Controller
          name='lastName'
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <TextField
              fullWidth
              label='Last Name'
              value={value || ''}
              onChange={onChange}
              error={!!error}
              helperText={error?.message}
              variant='outlined'
              size='medium'
              margin='normal'
              required
              className={styles.muiInputField}
            />
          )}
        />
        <Controller
          name='jobTitle'
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <TextField
              fullWidth
              label='Job Title'
              value={value || ''}
              onChange={onChange}
              error={!!error}
              helperText={error?.message}
              variant='outlined'
              size='medium'
              margin='normal'
              className={styles.muiInputField}
            />
          )}
        />
        {isEdit ? null : (
          <Controller
            name='primaryEmail'
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                fullWidth
                label='Email Address'
                value={value || ''}
                onChange={onChange}
                error={!!error}
                helperText={error?.message}
                variant='outlined'
                size='medium'
                margin='normal'
                required
                className={styles.muiInputField}
              />
            )}
          />
        )}
        <Controller
          name='userRole'
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Autocomplete
              fullWidth
              options={getRoleChoices(currentUser.userRole)}
              getOptionLabel={(option: any) => option?.label || ''}
              value={getRoleChoices(currentUser.userRole).find((choice) => choice.value === value) || null}
              onChange={(_, newValue) => onChange(newValue ? newValue.value : '')}
              renderInput={(params) => (
                <TextField {...params} label='Role' error={!!error} helperText={error?.message} variant='outlined' size='medium' margin='normal' required className={styles.muiInputField} />
              )}
              isOptionEqualToValue={(option, v) => option.value === v?.value}
            />
          )}
        />

        <div className={styles.formActions}>
          <LxCancelButton onCancel={onCancel} classNames='lxActionButton, lxActionButtonDefaultSize' />
          <LxSaveButton onSubmit={handleSubmit(onSubmit)} label={isEdit ? 'Save Changes' : 'Create Team Member'} classNames='lxActionButton,lxActionButtonFilled,lxActionButtonDefaultSize' />
        </div>
      </form>
      {accHasPrivacyMessage && canSeePrivacyMessage && <PrivacyNotice show={accHasPrivacyMessage && canSeePrivacyMessage} className={styles.privacyNotice} titleId='privacy-dialog-title' />}
    </div>
  )
}
